import { useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, pct, margeText, FAMILLES, UNITES } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const EMPTY = { code: "", nom: "", famille: FAMILLES[0], unite: UNITES[6], cout_unitaire_ht: "", prix_unitaire_ht: "", tva_pct: "20", description: "" };

export default function CatalogueView() {
  const { canSeeMargins, isAdmin } = useViewRole();
  const [items, setItems] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [q, setQ] = useState("");
  const [famille, setFamille] = useState("Toutes");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/catalogue").then((r) => setItems(r.data)).catch(() => {});
    api.get("/familles").then((r) => setFamilles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const familleNames = familles.length ? familles.map((f) => f.nom) : FAMILLES;

  const marge = (Number(form.prix_unitaire_ht) || 0) - (Number(form.cout_unitaire_ht) || 0);
  const tauxMarge = Number(form.prix_unitaire_ht) ? (marge / Number(form.prix_unitaire_ht)) * 100 : 0;

  const filtered = items.filter(
    (i) =>
      (famille === "Toutes" || i.famille === famille) &&
      (i.nom.toLowerCase().includes(q.toLowerCase()) || (i.code || "").toLowerCase().includes(q.toLowerCase()))
  );

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (i) => {
    setForm({ ...i, cout_unitaire_ht: i.cout_unitaire_ht, prix_unitaire_ht: i.prix_unitaire_ht, tva_pct: String(i.tva_pct) });
    setEditId(i.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.nom) return toast.error("Nom requis");
    const payload = {
      code: form.code, nom: form.nom, famille: form.famille, unite: form.unite,
      cout_unitaire_ht: Number(form.cout_unitaire_ht) || 0, prix_unitaire_ht: Number(form.prix_unitaire_ht) || 0,
      tva_pct: Number(form.tva_pct) || 0, description: form.description,
    };
    try {
      if (editId) { await api.put(`/catalogue/${editId}`, payload); toast.success("Prestation mise à jour"); }
      else { await api.post("/catalogue", payload); toast.success("Prestation ajoutée au catalogue"); }
      setOpen(false);
      load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/catalogue/${id}`); toast.success("Prestation supprimée"); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="catalogue-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Catalogue Prestations</h1>
          <p className="text-slate-400 text-sm mt-1">Base centralisée des prix et coûts unitaires réutilisables.</p>
        </div>
        <Button data-testid="add-prestation-button" onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Nouvelle prestation
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="catalogue-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une prestation…" className="pl-10 bg-[#111827] border-[#1E293B]" />
        </div>
        <Select value={famille} onValueChange={setFamille}>
          <SelectTrigger data-testid="catalogue-famille-filter" className="w-64 bg-[#111827] border-[#1E293B]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
            <SelectItem value="Toutes">Toutes les familles</SelectItem>
            {familleNames.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#1E293B]">
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-3 py-3 font-medium">Prestation</th>
                <th className="px-3 py-3 font-medium">Famille</th>
                <th className="px-3 py-3 font-medium text-center">Unité</th>
                {canSeeMargins && <th className="px-3 py-3 font-medium text-right">Coût HT</th>}
                <th className="px-3 py-3 font-medium text-right">Prix HT</th>
                {canSeeMargins && <th className="px-3 py-3 font-medium text-right">Marge</th>}
                <th className="px-3 py-3 font-medium text-right">TVA</th>
                <th className="px-3 py-3 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} className="border-b border-[#1E293B]/60 hover:bg-[#0B0F17]/50" data-testid={`catalogue-row-${i.code}`}>
                  <td className="px-4 py-2.5 font-mono-metric text-xs text-slate-500">{i.code}</td>
                  <td className="px-3 py-2.5 font-medium">{i.nom}</td>
                  <td className="px-3 py-2.5 text-slate-400 text-xs">{i.famille}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300">{i.unite}</span>
                  </td>
                  {canSeeMargins && <td className="px-3 py-2.5 text-right font-mono-metric text-orange-400">{eur2Small(i.cout_unitaire_ht)}</td>}
                  <td className="px-3 py-2.5 text-right font-mono-metric font-semibold">{eur2Small(i.prix_unitaire_ht)}</td>
                  {canSeeMargins && (
                    <td className={`px-3 py-2.5 text-right font-mono-metric font-semibold ${margeText(i.taux_marge_pct)}`}>{pct(i.taux_marge_pct)}</td>
                  )}
                  <td className="px-3 py-2.5 text-right text-slate-400 font-mono-metric text-xs">{i.tva_pct}%</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`catalogue-edit-${i.code}`} onClick={() => openEdit(i)} className="text-slate-400 hover:text-amber-400 p-1"><Pencil className="h-4 w-4" /></button>
                    {isAdmin && (
                      <button data-testid={`catalogue-delete-${i.code}`} onClick={() => remove(i.id)} className="text-slate-400 hover:text-red-400 p-1 ml-1"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500"><Layers className="h-5 w-5 mx-auto mb-2" />Aucune prestation trouvée.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head">{editId ? "Modifier la prestation" : "Nouvelle prestation"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-slate-400 text-xs">Nom</Label>
                <Input data-testid="prestation-input-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Famille</Label>
                <Select value={form.famille} onValueChange={(v) => setForm({ ...form, famille: v })}>
                  <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    {familleNames.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Unité</Label>
                <Select value={form.unite} onValueChange={(v) => setForm({ ...form, unite: v })}>
                  <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    {UNITES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Coût HT (€)</Label>
                <Input type="number" data-testid="prestation-input-cout" value={form.cout_unitaire_ht} onChange={(e) => setForm({ ...form, cout_unitaire_ht: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] font-mono-metric" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Prix HT (€)</Label>
                <Input type="number" data-testid="prestation-input-prix" value={form.prix_unitaire_ht} onChange={(e) => setForm({ ...form, prix_unitaire_ht: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] font-mono-metric" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">TVA (%)</Label>
                <Input type="number" value={form.tva_pct} onChange={(e) => setForm({ ...form, tva_pct: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] font-mono-metric" />
              </div>
            </div>
            <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3 flex items-center justify-between">
              <span className="text-xs text-slate-400">Marge unitaire calculée</span>
              <div className="text-right">
                <span className="font-mono-metric font-semibold text-slate-100">{eur(marge)}</span>
                <span className={`ml-2 font-mono-metric font-semibold ${margeText(tauxMarge)}`}>{pct(tauxMarge)}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] resize-none" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Annuler</Button>
            <Button data-testid="prestation-save-button" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function eur2Small(n) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
}
